import {
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ElementType,
  type ForwardedRef,
  forwardRef,
  type ReactElement,
  type ReactNode,
} from 'react'
import { Card, type CardProps } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { type CommonCardBackground } from '@DS/components/components/foundations/shapes/card/types'

import * as styles from './styles'

export type BasicCardProps<Component extends ElementType = 'div'> = Omit<CardProps<Component>, 'children'> & {
  hasError?: boolean
  borderColor?: CSSProperties['borderColor']
  background?: CommonCardBackground
  backgroundColor?: CSSProperties['backgroundColor']
  isFullWidth?: boolean
  children?: ReactNode
} & ComponentPropsWithoutRef<Component> & { component?: Component }

const BasicCard = forwardRef(
  <Component extends ElementType>(
    {
      children,
      background = 'paper',
      backgroundColor,
      hasError = false,
      isFullWidth = false,
      borderColor,
      ...rest
    }: BasicCardProps<Component>,
    ref: ForwardedRef<HTMLDivElement>,
  ): ReactElement | null => {
    const theme = useTheme()

    return (
      <Card
        {...rest}
        ref={ref}
        css={styles.basicCard(
          theme,
          background,
          backgroundColor,
          hasError ? theme.palette.error.main : borderColor,
          isFullWidth,
        )}
      >
        {children}
      </Card>
    )
  },
)

export default BasicCard
